import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Shield,
  Activity,
  Heart,
  PiggyBank,
  CalendarCheck,
  Bell,
  ClipboardCheck,
  Sparkles,
  ArrowRight,
} from "lucide-react";

const emotionalArmor = [
  {
    icon: Shield,
    title: "The Emotional Armor",
    body: "You catch yourself putting up walls, holding partners at arm's length, or predicting abandonment before it happens.",
  },
  {
    icon: Activity,
    title: "The High-Functioning Anxiety",
    body: "Your mind is constantly scanning for threat, over-analyzing text messages, or running through worst-case scenarios.",
  },
  {
    icon: Heart,
    title: "The Relational PTSD",
    body: "Even when you know intellectually that you are safe with someone, your body stays stuck in fight-or-flight, hyper-vigilant and unable to fully trust or let love in.",
  },
];

const howItWorks = [
  {
    icon: ClipboardCheck,
    title: "Choose Your Preferred Intensive Level",
    body: "Select the virtual KAP intensive format (complete with somatic IFS parts work, EMDR, and spiritual integration) that aligns with your healing goals.",
  },
  {
    icon: PiggyBank,
    title: "Set Your Custom Monthly Contribution",
    body: "Establish an automated monthly contribution rate that feels comfortable and completely sustainable within your lifestyle.",
  },
  {
    icon: Bell,
    title: "Receive Monthly Progress Updates",
    body: "Each month, you will receive an automated balance update reflecting your accumulated total toward your chosen intensive.",
  },
  {
    icon: CalendarCheck,
    title: "Seamless Scheduling",
    body: "The moment your savings balance reaches the full fee of your preferred intensive, our intake team will personally contact you to schedule your comprehensive medical assessment and initial dosing sessions.",
  },
];

const whyGreat = [
  {
    title: "Zero Financial Shock",
    body: "Hyper-independence often stems from a deep need for control and safety. By breaking your investment into manageable monthly contributions, you preserve your financial safety net while taking decisive action toward your recovery.",
  },
  {
    title: "A Concrete Commitment to Yourself",
    body: "When you live in survival mode, prioritizing your own emotional well-being usually comes last on the priority list. Setting up a dedicated savings plan acts as a tangible promise to your future self — a commitment that your peace, safety, and relational freedom matter.",
  },
  {
    title: "Immediate Nervous System Relief",
    body: "The simple act of enrolling in a structured plan creates psychological safety. Knowing that your intensive is officially in motion allows your nervous system to begin relaxing the moment you take the first step.",
  },
];

const whatHappens = [
  {
    title: "Sublingual Ketamine Dosing",
    body: "Creates temporary neuroplasticity, allowing your brain to bypass logical defenses and cognitive control.",
  },
  {
    title: "Somatic IFS & EMDR",
    body: "Directly targets and releases stored relational trauma, emotional armor, and attachment wounds held within the physical body.",
  },
  {
    title: "Spiritual & Relational Integration",
    body: "Translates your deep medicine insights into felt, real-world behavioral changes, enabling you to build authentic, connected relationships without losing your autonomy.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export const KetamineSavingsPlan = ({ background = "bg-secondary/30", variant = "full" }) => {
  if (variant === "teaser") {
    return (
      <section
        className={`py-24 md:py-32 px-6 md:px-12 ${background}`}
        data-testid="ketamine-savings-plan"
      >
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Beyond Survival Mode
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              How Our Ketamine Intensive Savings Plan Helps Hyper-Independent Women Heal{" "}
              <span className="italic text-primary">Relational Trauma</span>
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-6 text-base md:text-lg text-foreground/80 leading-[1.85] mb-10">
            <p>
              For high-achieving, hyper-independent women, asking for help often feels infinitely harder than carrying everything alone. When traditional talk therapy hits a ceiling, <strong className="text-foreground">Virtual Ketamine-Assisted Psychotherapy (KAP) Intensives</strong> work beneath cognitive armor to reset the autonomic nervous system at the root.
            </p>
            <p>
              We understand that for women accustomed to managing every financial detail on their own terms, investing in specialized, private-pay trauma care requires clear planning and freedom. That is why we offer our <strong className="text-foreground">Ketamine Intensive Savings Plan</strong> &mdash; a structured, zero-pressure path designed to fit your financial timeline.
            </p>
            <p>
              Choose your intensive tier, set a comfortable monthly contribution, receive automated progress updates, and our intake team reaches out the moment your savings meet the full fee. No sudden financial shock &mdash; just a concrete commitment to your future self and immediate nervous system relief the day you enroll.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="text-center">
            <Link to="/fees">
              <Button
                size="lg"
                className="rounded-full px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="savings-plan-teaser-cta"
              >
                Read the Full Plan
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`py-24 md:py-32 px-6 md:px-12 ${background}`}
      data-testid="ketamine-savings-plan"
    >
      <div className="max-w-4xl mx-auto">
        {/* Eyebrow + H2 */}
        <motion.div {...fadeUp} className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
            Beyond Survival Mode
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
            How Our Ketamine Intensive Savings Plan Helps Hyper-Independent Women Heal{" "}
            <span className="italic text-primary">Relational Trauma</span>
          </h2>
        </motion.div>

        {/* Intro */}
        <motion.div {...fadeUp} className="space-y-6 text-base md:text-lg text-foreground/80 leading-[1.85] mb-12">
          <p>
            For high-achieving, hyper-independent women, asking for help often feels infinitely harder than carrying everything alone.
          </p>
          <p>
            On the outside, you have it all under control — a thriving career, sharp focus, and a reputation for being the person who fixes everything. But inside your relationships, a very different story plays out:
          </p>
        </motion.div>

        {/* 3-column pattern signals */}
        <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {emotionalArmor.map((item) => (
            <Card
              key={item.title}
              className="p-6 bg-white border-border/40 rounded-2xl"
              data-testid={`savings-signal-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="p-3 rounded-full bg-primary/10 inline-block">
                <item.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-foreground mt-4">
                {item.title}
              </h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                {item.body}
              </p>
            </Card>
          ))}
        </motion.div>

        {/* Bridge paragraphs */}
        <motion.div {...fadeUp} className="space-y-6 text-base md:text-lg text-foreground/80 leading-[1.85] mb-16">
          <p>
            When traditional talk therapy hits a ceiling, <strong className="text-foreground">Virtual Ketamine-Assisted Psychotherapy (KAP) Intensives</strong> work beneath cognitive armor to reset the autonomic nervous system at the root.
          </p>
          <p>
            However, we understand that for women accustomed to managing every financial detail on their own terms, investing in specialized, private-pay trauma care requires clear planning and freedom. That is why we offer our <strong className="text-foreground">Ketamine Intensive Savings Plan</strong> — a structured, zero-pressure path designed to fit your financial timeline.
          </p>
        </motion.div>

        {/* What is the savings plan */}
        <motion.div {...fadeUp} className="mb-12">
          <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground mb-4">
            What is the Ketamine Intensive Savings Plan?
          </h3>
          <div className="space-y-4 text-base md:text-lg text-foreground/80 leading-[1.85]">
            <p>
              Think of this as a structured, self-directed program designed specifically for women who prefer to plan ahead and maintain full financial control over their care.
            </p>
            <p>
              Rather than taking on sudden financial stress or delaying your healing indefinitely, our savings plan allows you to incrementally fund your immersive virtual KAP intensive over time.
            </p>
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div {...fadeUp} className="mb-16">
          <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground mb-8">
            How It Works
          </h3>
          <ol className="space-y-5">
            {howItWorks.map((step, idx) => (
              <li
                key={step.title}
                className="flex items-start gap-5 p-5 bg-white border border-border/40 rounded-2xl"
                data-testid={`savings-step-${idx + 1}`}
              >
                <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary text-primary-foreground font-serif text-lg font-semibold flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <h4 className="font-serif text-lg font-semibold text-foreground flex items-center gap-2">
                    <step.icon className="h-4 w-4 text-primary" />
                    {step.title}
                  </h4>
                  <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </motion.div>

        {/* Why Great */}
        <motion.div {...fadeUp} className="mb-16">
          <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground mb-8">
            Why This Approach Is Great For Hyper-Independent Women
          </h3>
          <div className="space-y-5">
            {whyGreat.map((item, idx) => (
              <div
                key={item.title}
                className="p-6 bg-accent/5 border border-accent/30 rounded-2xl"
                data-testid={`savings-why-${idx + 1}`}
              >
                <h4 className="font-serif text-lg font-semibold text-foreground">
                  {idx + 1}. {item.title}
                </h4>
                <p className="text-sm md:text-base text-muted-foreground mt-2 leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* What Happens */}
        <motion.div {...fadeUp} className="mb-16">
          <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground mb-4">
            What Happens During Your Virtual KAP Intensive?
          </h3>
          <p className="text-base md:text-lg text-foreground/80 leading-[1.85] mb-6">
            Once your savings threshold is met and your sessions are scheduled, your virtual intensive bridges modern neuroscience with deep somatic integration across Florida, Washington, D.C., and Maine:
          </p>
          <ul className="space-y-4">
            {whatHappens.map((item) => (
              <li
                key={item.title}
                className="flex items-start gap-4"
                data-testid={`savings-modality-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Sparkles className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-foreground">{item.title}</p>
                  <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                    {item.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Closing CTA */}
        <motion.div {...fadeUp} className="rounded-3xl bg-primary text-primary-foreground p-8 md:p-12 text-center">
          <h3 className="font-serif text-2xl md:text-3xl font-semibold">
            Take the First Step on Your Terms
          </h3>
          <p className="mt-4 opacity-90 leading-relaxed max-w-2xl mx-auto">
            You don't have to carry the weight of hyper-independence forever, and you don't have to compromise your financial boundary to access high-level trauma care.
          </p>
          <p className="mt-6 font-serif text-lg italic opacity-95">
            Ready to start your path toward embodied peace?
          </p>
          <p className="mt-4 text-sm opacity-85 max-w-2xl mx-auto leading-relaxed">
            Visit our practice portal today to select your KAP intensive tier, set up your monthly savings plan, or schedule an initial consultation to discuss out-of-network reimbursement support via Thrizer.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="rounded-full px-8 py-6 bg-white text-primary hover:bg-white/90"
                data-testid="savings-plan-cta-book"
              >
                Book Free 40-min Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/fees">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 border-white/40 text-white hover:bg-white/10 hover:text-white"
                data-testid="savings-plan-cta-tiers"
              >
                View Intensive Tiers
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default KetamineSavingsPlan;
